import React, { useCallback, useState } from 'react'

import whitePlus from '../../assets/images/icons/white-plus.svg'
import LetsTalkCta from '../LetsTalkCta'

/**
 * Reusable “Capabilities” section for service detail pages (tabs + panel).
 *
 * @param {string} [sectionTitle]
 * @param {{ id: string, label: string, paragraphs: string[] }[]} tabs
 * @param {boolean} [contactCtaIsEn] — when set, shows centered “Let’s talk” / contact CTA
 * @param {string} [idBase] — optional stable id prefix for ARIA hooks
 * @param {boolean} [nested] — render as inner block (no section/container wrapper)
 * @param {boolean} [showHeading] — show sectionTitle h2
 * @param {string} [defaultActiveId] — initial active tab id
 */
const ServiceCapabilities = ({
  sectionTitle = 'Capabilities that drive Digital Products.',
  tabs,
  contactCtaIsEn,
  idBase = 'service-capabilities',
  nested = false,
  showHeading = true,
  defaultActiveId,
}) => {
  const baseId = idBase
  const [activeId, setActiveId] = useState(() => defaultActiveId ?? tabs[0]?.id ?? '')

  const onKeyDown = useCallback(
    e => {
      if (!tabs.length) return
      const idx = tabs.findIndex(t => t.id === activeId)
      if (idx < 0) return
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        const next = tabs[(idx + 1) % tabs.length]
        setActiveId(next.id)
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        const next = tabs[(idx - 1 + tabs.length) % tabs.length]
        setActiveId(next.id)
      }
    },
    [tabs, activeId]
  )

  if (!tabs.length) {
    return null
  }

  const content = (
    <>
      {showHeading && sectionTitle ? (
        <h2 id={`${baseId}-capabilities-heading`} className="service-capabilities__heading">
          {sectionTitle}
        </h2>
      ) : null}
      <div className="service-capabilities__layout">
        <div
          className="service-capabilities__tabs"
          role="tablist"
          aria-label={sectionTitle || 'Capabilities'}
          onKeyDown={onKeyDown}
        >
          {tabs.map(tab => {
            const selected = tab.id === activeId
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`${baseId}-tab-${tab.id}`}
                className={
                  'service-capabilities__tab' +
                  (selected ? ' service-capabilities__tab--active' : '')
                }
                aria-selected={selected}
                aria-controls={`${baseId}-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveId(tab.id)}
              >
                <span className="service-capabilities__tab-label">{tab.label}</span>
                {!selected && (
                  <span className="service-capabilities__tab-plus" aria-hidden="true">
                    <img src={whitePlus} alt="" width={19} height={19} decoding="async" />
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <div className="service-capabilities__panel-shell">
          <div className="service-capabilities__panel-stack">
            {tabs.map(tab => {
              const selected = tab.id === activeId
              return (
                <div
                  key={tab.id}
                  className={
                    'service-capabilities__panel' +
                    (selected
                      ? ' service-capabilities__panel--active'
                      : ' service-capabilities__panel--inactive')
                  }
                  role="tabpanel"
                  id={`${baseId}-panel-${tab.id}`}
                  aria-labelledby={`${baseId}-tab-${tab.id}`}
                  aria-hidden={!selected ? true : undefined}
                  tabIndex={selected ? 0 : -1}
                >
                  {tab.paragraphs.map((text, i) => (
                    <p key={i} className="service-capabilities__text">
                      {text}
                    </p>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {contactCtaIsEn !== undefined && (
        <div className="service-capabilities__cta">
          <LetsTalkCta isEn={contactCtaIsEn} tone="onDark" paintDrip={false} />
        </div>
      )}
    </>
  )

  if (nested) {
    return <div className="service-capabilities service-capabilities--nested">{content}</div>
  }

  return (
    <section
      className="service-capabilities"
      aria-labelledby={`${baseId}-capabilities-heading`}
    >
      <div className="content container">{content}</div>
    </section>
  )
}

export default ServiceCapabilities
